import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import useAsync from 'react-use/esm/useAsync';
import { Entity } from '@backstage/catalog-model';
import { Select } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  EntityTypeFilter,
  EntityTypePicker,
  catalogApiRef,
  catalogReactTranslationRef,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import {
  FilterMap,
  countAvailableValues,
  sameValues,
  toStringArray,
  withSelected,
} from './availableValues';

const getType = (entity: Entity) =>
  typeof entity.spec?.type === 'string' ? [entity.spec.type.toLowerCase()] : [];

const FacetedEntityTypePickerContent = () => {
  const { t } = useTranslationRef(catalogReactTranslationRef);
  const catalogApi = useApi(catalogApiRef);
  const { filters, queryParameters, updateFilters, backendEntities } =
    useEntityList();
  const filterMap = filters as unknown as FilterMap;
  const kind = filters.kind?.value;

  const queryTypes = useMemo(
    () => toStringArray(queryParameters.type).map(type => type.toLowerCase()),
    [queryParameters.type],
  );
  const [selectedTypes, setSelectedTypes] = useState<string[]>(() =>
    queryTypes.length ? queryTypes : filters.type?.getTypes() ?? [],
  );

  useEffect(() => {
    if (!queryTypes.length) {
      return;
    }
    setSelectedTypes(previous =>
      sameValues(previous, queryTypes) ? previous : queryTypes,
    );
  }, [queryTypes]);

  const previousKind = useRef(kind);
  useEffect(() => {
    if (previousKind.current !== undefined && previousKind.current !== kind) {
      setSelectedTypes([]);
    }
    previousKind.current = kind;
  }, [kind]);

  useEffect(() => {
    updateFilters({
      type: selectedTypes.length
        ? new EntityTypeFilter(selectedTypes)
        : undefined,
    });
  }, [selectedTypes, updateFilters]);

  const backendScope = useMemo(
    () =>
      Object.entries(filterMap)
        .filter(
          ([key, filter]) =>
            key !== 'type' && filter?.getCatalogFilters && !filter.filterEntity,
        )
        .reduce<Record<string, string | symbol | (string | symbol)[]>>(
          (scope, [, filter]) => ({
            ...scope,
            ...filter?.getCatalogFilters?.(),
          }),
          {},
        ),
    [filterMap],
  );
  const backendScopeKey = JSON.stringify(backendScope, (_key, value) =>
    typeof value === 'symbol' ? value.toString() : value,
  );
  const typeFilterActive = selectedTypes.length > 0;

  const { value: entitiesWithoutType } = useAsync(async () => {
    if (!typeFilterActive) {
      return undefined;
    }
    const response = await catalogApi.getEntities({ filter: backendScope });
    return response.items;
  }, [catalogApi, typeFilterActive, backendScopeKey]);

  const sourceEntities = typeFilterActive
    ? entitiesWithoutType ?? backendEntities
    : backendEntities;
  const counts = useMemo(
    () => countAvailableValues(sourceEntities, filterMap, 'type', getType),
    [sourceEntities, filterMap],
  );
  const types = useMemo(
    () => withSelected(counts, selectedTypes),
    [counts, selectedTypes],
  );

  if (!types.length) {
    return null;
  }

  const items = [
    { value: 'all', label: t('entityTypePicker.optionAllTitle') },
    ...types.map(type => ({
      value: type,
      label: `${type} (${counts[type] ?? 0})`,
    })),
  ];

  return (
    <Box pb={1} pt={1}>
      <Select
        label={t('entityTypePicker.title')}
        items={items}
        selected={selectedTypes[0] ?? 'all'}
        onChange={value =>
          setSelectedTypes(value === 'all' ? [] : [String(value)])
        }
      />
    </Box>
  );
};

export const FacetedEntityTypePicker = () => {
  const { paginationMode } = useEntityList();
  if (paginationMode !== 'none') {
    return <EntityTypePicker />;
  }
  return <FacetedEntityTypePickerContent />;
};
